import type { CharacterChoice } from '../characters/characterSelection';
import type { UiConfig } from '../config/ui.config';
import { CharacterSelect } from './CharacterSelect';
import { el, formatNumber } from './dom';
import { ICONS, icon } from './icons';
import { LeaderboardList, type LeaderboardEntry, type OwnLeaderboardEntry } from './Leaderboard';
import { menuButton, menuFooter, menuLink, menuTitle } from './MenuParts';
import type { SoundMode } from '../services/ProgressStore';
import { MusicToggle } from './MusicToggle';
import { SponsorBadge } from './SponsorBadge';
import type { AdSponsor } from '../advertising/AdProvider';

export interface StartScreenActions {
  readonly onStart: () => void;
  readonly onSelect: (id: string) => void;
  /** Arrow buttons: previous (-1) / next (+1) character. */
  readonly onStep: (direction: -1 | 1) => void;
  readonly onShowLeaderboard: () => void;
  /** "So funktioniert’s" under the start button. */
  readonly onShowHowTo: () => void;
  readonly onToggleMusic: () => void;
}

const NOTE_SIGNED_OUT = 'Mit dem ructec-Konto deines Unternehmens anmelden – dann zählt jeder Lauf für die Bestenliste.';

/**
 * Start screen on the user's start scene (both runners are part of the picture). Desktop / landscape: one
 * panel on the left – logo, both character cards with their portrait, start button – the scene stays free on
 * the right; top right the music switch and the leaderboard preview (top 3, personal best, sign-up). Phones in
 * portrait (CSS): logo on top, the scene in the middle, at the bottom one card at a time as a carousel with
 * arrows and dots, start button below, leaderboard and sign-up as two links.
 */
export class StartScreen {
  readonly element: HTMLDivElement;
  private readonly select: CharacterSelect;
  private readonly dots = new Map<string, HTMLSpanElement>();
  private readonly prev: HTMLButtonElement;
  private readonly next: HTMLButtonElement;
  private readonly order: readonly string[];
  private readonly music: MusicToggle;
  private readonly leaders: LeaderboardList;
  private readonly best: HTMLElement;
  private readonly sponsor: SponsorBadge;
  /** "Anmelden" links (sign-in on ructec.com, back with a Spielausweis) – hidden while signed in. */
  private readonly loginLinks: HTMLAnchorElement[] = [];
  private readonly leadersNote: HTMLElement;

  constructor(parent: HTMLElement, ui: UiConfig, choices: readonly CharacterChoice[], actions: StartScreenActions) {
    this.order = choices.map((choice) => choice.id);
    this.element = el('div', 'screen menu menu-start', undefined, parent);
    const panel = el('div', 'start-panel', undefined, this.element);
    menuTitle(panel, ui);
    this.sponsor = new SponsorBadge(panel, 'is-start');

    const stage = el('div', 'start-stage', undefined, panel);
    this.prev = this.arrow(stage, 'is-prev', ICONS.chevronLeft, 'Vorherige Figur', () => actions.onStep(-1));
    this.select = new CharacterSelect(stage, choices, actions.onSelect, 'is-start');
    this.next = this.arrow(stage, 'is-next', ICONS.chevronRight, 'Nächste Figur', () => actions.onStep(1));

    const dots = el('div', 'start-dots', undefined, panel);
    dots.setAttribute('aria-hidden', 'true');
    for (const id of this.order) this.dots.set(id, el('span', 'start-dot', undefined, dots));

    // start + how-to: one below the other – side by side on low landscape screens (CSS), where height is short
    const actionsRow = el('div', 'start-actions', undefined, panel);
    const start = el('button', 'screen-button start-button', undefined, actionsRow);
    start.type = 'button';
    const label = el('span', 'start-button-label', 'Los geht’s', start);
    label.append(icon(ICONS.play, 'start-button-play'));
    el('small', 'screen-key-hint', 'Leertaste / Tippen', start);
    start.addEventListener('click', (event) => {
      event.stopPropagation();
      actions.onStart();
    });
    start.addEventListener('pointerdown', (event) => event.stopPropagation());

    // user 2026-10-03: "eine Infoseite … auf der Startseite … unter der Taste los geht's"
    const howTo = menuButton(actionsRow, '', actions.onShowHowTo, 'is-secondary start-howto');
    howTo.title = 'So funktioniert’s';
    howTo.append(icon(ICONS.help, 'start-howto-icon'));
    el('span', 'start-howto-label', 'So funktioniert’s', howTo);

    // phones: leaderboard and sign-up as links under the start button (the preview box has no room there)
    const links = el('nav', 'start-links', undefined, panel);
    links.setAttribute('aria-label', 'Bestenliste und Anmelden');
    menuButton(links, 'Bestenliste', actions.onShowLeaderboard, 'is-link');
    this.loginLinks.push(this.loginLink(links, ui, 'is-link'));

    el('p', 'start-hint screen-touch-hint', 'Tippen zum Starten', panel);
    el('p', 'start-hint screen-key-hint', 'A / D oder ← → Spur · W / ↑ Springen · S / ↓ Rutschen · ESC Pause', panel);

    this.music = new MusicToggle(this.element, 'menu-music', actions.onToggleMusic);

    const preview = el('aside', 'start-leaders', undefined, this.element);
    preview.setAttribute('aria-labelledby', 'start-leaders-title');
    const head = el('h2', 'start-leaders-title', undefined, preview);
    head.id = 'start-leaders-title';
    head.append(icon(ICONS.trophy, 'start-leaders-icon'), 'Bestenliste');
    el('span', 'start-leaders-tag', `Top ${ui.leaderboard.previewSize}`, head);
    this.leaders = new LeaderboardList(preview, ui.leaderboard.previewSize, 'is-preview');
    this.leadersNote = el('p', 'start-leaders-note', NOTE_SIGNED_OUT, preview);
    const bestLine = el('p', 'start-leaders-best', 'Dein Rekord: ', preview);
    this.best = el('strong', undefined, '0', bestLine);
    const previewActions = el('div', 'start-leaders-actions', undefined, preview);
    menuButton(previewActions, 'Ganze Liste', actions.onShowLeaderboard, 'is-secondary');
    this.loginLinks.push(this.loginLink(previewActions, ui, 'is-primary'));

    menuFooter(this.element, ui.footer);
  }

  setSelected(id: string): void {
    this.select.setSelected(id);
    for (const [dotId, dot] of this.dots) dot.classList.toggle('is-selected', dotId === id);
    const index = this.order.indexOf(id);
    this.prev.disabled = index <= 0;
    this.next.disabled = index >= this.order.length - 1;
  }

  setSoundMode(mode: SoundMode): void {
    this.music.setMode(mode);
  }

  /** "sponsored by" under the logo – the company whose booking sponsors the next run. */
  setSponsor(sponsor: AdSponsor | null): void {
    this.sponsor.set(sponsor);
  }

  setBestScore(score: number): void {
    this.best.textContent = formatNumber(score);
  }

  setLeaderboard(entries: readonly LeaderboardEntry[], own: OwnLeaderboardEntry | null): void {
    this.leaders.setEntries(entries, own);
  }

  /** Signed in for the leaderboard: "angemeldet als …" instead of the sign-in link. */
  setSignedIn(signedIn: boolean, player: { readonly company: string; readonly name: string } | null): void {
    for (const link of this.loginLinks) link.hidden = signedIn;
    this.leadersNote.textContent = signedIn
      ? player ? `Angemeldet als ${player.name} · ${player.company} – jeder Lauf zählt.` : 'Angemeldet – jeder Lauf zählt.'
      : NOTE_SIGNED_OUT;
  }

  /**
   * Sign-in on ructec.com in THIS tab: already signed in on the portal → straight back with a Spielausweis (the game
   * cannot see the portal's sign-in itself – another domain).
   */
  private loginLink(parent: HTMLElement, ui: UiConfig, variant: string): HTMLAnchorElement {
    const link = menuLink(parent, 'Anmelden', ui.account.loginUrl, variant);
    link.target = '_self';
    link.removeAttribute('rel');
    return link;
  }

  private arrow(parent: HTMLElement, className: string, markup: string, label: string, onClick: () => void): HTMLButtonElement {
    const button = el('button', `start-arrow ${className}`, undefined, parent);
    button.type = 'button';
    button.setAttribute('aria-label', label);
    button.append(icon(markup, 'start-arrow-icon'));
    button.addEventListener('click', (event) => {
      event.stopPropagation();
      onClick();
    });
    button.addEventListener('pointerdown', (event) => event.stopPropagation());
    return button;
  }
}
